// Utility functions for number formatting
export const formatCurrency = (value: number, suffix: string | null = null): string => {
  if (Math.abs(value) < 1000) {
    // Less than 1,000: display without decimal
    return new Intl.NumberFormat("fr-FR", {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(value) + (suffix === null ? "€" : "");
  } else if (Math.abs(value) < 1000000) {
    // Less than 1,000,000: display in thousands with 2 decimals, remove if .00
    const formattedValue = new Intl.NumberFormat("fr-FR", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(value / 1000);
    const cleanValue = formattedValue.replace(/,00$/, "");
    return cleanValue + (suffix === null ? "k€" : "");
  } else if (Math.abs(value) < 1000000000) {
    // Less than 1,000,000,000: display in millions with 2 decimals, remove if .00
    const formattedValue = new Intl.NumberFormat("fr-FR", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(value / 1000000);
    const cleanValue = formattedValue.replace(/,00$/, "");
    return cleanValue + (suffix === null ? "M€" : "");
  } else {
    // 1 billion or more: display in billions with 2 decimals, remove if .00
    const formattedValue = new Intl.NumberFormat("fr-FR", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(Math.abs(value) / 1000000000);
    const cleanValue = formattedValue.replace(/,00$/, "");
    return cleanValue + (suffix === null ? "Md€" : "");
  }
};

export const formatKvalue = (value: number | null | undefined, suffix: string | null = null): string => {
  if (value === null || value === undefined || isNaN(value)) return "-";
  
  if (Math.abs(value) >= 1000) {
    const formattedValue = new Intl.NumberFormat("fr-FR", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(value / 1000);
    const cleanValue = formattedValue.replace(/,00$/, "");
    return cleanValue + (suffix === null ? "M€" : "");
  } else if (Math.abs(value) > 0 && Math.abs(value) < 1) {
    const formattedValue = new Intl.NumberFormat("fr-FR", {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(value * 1000);
    const cleanValue = formattedValue.replace(/,00$/, "");
    return cleanValue + (suffix === null ? "€" : "");
  }
  
  const formattedValue = new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(value);
  const cleanValue = formattedValue.replace(/,00$/, "");
  return cleanValue + (suffix === null ? "K€" : "");
};


export const formatKvalueCASector = (value: number, suffix: string | null = null, space: boolean = false): string => {
  if (value === null || value === undefined || isNaN(value)) return "-";

  let finalValue = value;
  let defaultSuffix = "k€";
  let maxFractionDigits = 2;
  let minFractionDigits = 2;
  const absValue = Math.abs(value);

  // Cas Millions : Supérieur ou égal à 1 000 (K)
  if (absValue >= 1000) {
    finalValue = value / 1000;
    defaultSuffix = "M€";
  } 
  // Cas Euros bruts : Strictement inférieur à 1 (K) et différent de 0
  else if (absValue > 0 && absValue < 1) {
    finalValue = value * 1000;
    defaultSuffix = "€";
    maxFractionDigits= 0;
    minFractionDigits= 0;
  }
  // Cas Zéro exact
  else if (absValue === 0) {
    defaultSuffix = "";
  }

  // Formatage propre à la française
  const formattedNumber = new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: minFractionDigits, // Pas de ,00 inutile
    maximumFractionDigits: maxFractionDigits, // Maximum 2 décimales si nécessaire
  }).format(finalValue).replace(/,00$/, ""); // Supprimer les ,00 inutiles

  // Application du suffixe (le suffixe personnalisé écrase le suffixe par défaut)
  const displaySuffix = suffix !== null ? suffix : defaultSuffix;

  // Gestion de l'espace insécable avant le suffixe pour respecter la typo française
  return `${formattedNumber}${space ? "\u00A0" : ""}${displaySuffix}`.trim();
};

export const getCurrencySuffix = (value: number): string => {
  const kValue = value*1000;
  if (Math.abs(kValue) < 1000) {
    return "€";
  } else if (Math.abs(kValue) < 1000000) {
    return "k€";
  } else if (Math.abs(kValue) < 1000000000) {
    return "M€";
  } else {
    return "Md€";
  }
}

export const formatPercentage = (value: number | null, suffix: string = "%", space: boolean = false): string => {
  if (value === null) {
    return "-";
  } else if (value === 0) {
    return "0" + (space ? "\u00A0" : "") + suffix;
  } else if (Math.abs(value) < 1) {
    // Less than 1%: display with 2 decimals only if they exist
    return new Intl.NumberFormat("fr-FR", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(value) + (space ? "\u00A0" : "") + suffix;
  } else {
    // 1% or more: display with 1 decimal only if it exists
    return new Intl.NumberFormat("fr-FR", {
      maximumFractionDigits: 1,
      minimumFractionDigits: 0,
    }).format(value) + (space ? "\u00A0" : "") + suffix;
  }
};

export const displayValue = (value: number | null | undefined, formatter: (value: number) => string) => {
    return value == null || value === 0 ? "-" : formatter(value);
  };

export const fmtEuro = (n?: number) =>
  (n || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + "K€";

export const fmtPct = (n?: number) => {
  const v = n || 0;
  return (v > 0 ? "+" : "") + v.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " %";
};

export const fmtPct1Decimal = (n?: number) => {
  const v = n || 0;
  return (v > 0 ? "+" : "") + v.toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + " %";
};

export const fmtNum = (n?: number) =>
  ((n || 0) / 1000).toLocaleString("fr-FR", {
    maximumFractionDigits: 2,
  }) + "K";

export const pctColor = (n?: number) => (n || 0) >= 0 ? "#1d9e75" : "#e24b4a";


function formatMobileCaValue(value: number | null): string {
  if (value === null || value === undefined || isNaN(value)) {
    return "-";
  }

  return `${new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(value * 1000)}\u00A0€`;
}

function formatPercent(value: number | null): string {
  if (value === null) return "";
  const formatted = formatPercentage(value, "%", true);
  return value >= 0 ? `+${formatted}` : formatted;
}

function parseFormattedCurrencyToKValue(value: string): number {
  if (!value || value === "-") {
    return 0;
  }

  const normalized = value
    .replace(/\u00A0/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const amountMatch = normalized.match(/[-+]?\d+[\d\s]*(?:,\d+)?/);
  if (!amountMatch) {
    return 0;
  }

  const numericPart = amountMatch[0]
    .replace(/\s/g, "")
    .replace(",", ".");

  const amount = Number(numericPart);
  if (Number.isNaN(amount)) {
    return 0;
  }

  const lower = normalized.toLowerCase();
  if (lower.includes("md€")) {
    return amount * 1000000;
  }

  if (lower.includes("m€")) {
    return amount * 1000;
  }

  if (lower.includes("k€")) {
    return amount;
  }

  if (lower.includes("€")) {
    return amount / 1000;
  }

  return amount;
}

export {
  formatMobileCaValue,
  formatPercent,
  parseFormattedCurrencyToKValue,
};

export function formatGap(value: number | null): string {
  if (value === null || value === 0) return "-";
  const formatted = formatKvalueCASector(value, null, true);
  return value > 0 ? `+${formatted}` : formatted;
};

export const formatKiloEuro = (value: number, withSign = false): string => {
  const formatted = value.toLocaleString('fr-FR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const prefix = withSign && value > 0 ? '+' : '';
  return `${prefix}${formatted}k€`;
};

export const formatPercentageVal = (value: number): string => {
  const percent = value * 100;
  const formatted = percent.toLocaleString('fr-FR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const prefix = percent > 0 ? '+' : '';
  return `${prefix}${formatted}%`;
};